export type ApiOk<T> = { ok: true; data: T };
// `body` carries the raw parsed error response so callers can read structured
// fields the flattened `error` string drops (e.g. recovery's remainingAttempts,
// lockedUntil). Optional + additive — existing callers that only read `error`
// are unaffected.
export type ApiErr = { ok: false; error: string; status?: number; body?: unknown };
export type ApiResult<T> = ApiOk<T> | ApiErr;

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

async function readJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

interface FetchOptions {
  signal?: AbortSignal;
}

export async function apiGet<T>(path: string, opts?: FetchOptions): Promise<ApiResult<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { "Accept": "application/json" },
    signal: opts?.signal
  });
  const body = await readJsonSafe(res);
  if (!res.ok) return { ok: false, error: toErrorMessage(body) ?? res.statusText, status: res.status, body };
  return { ok: true, data: body as T };
}

export async function apiPost<T>(path: string, payload: unknown): Promise<ApiResult<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify(payload)
  });
  const body = await readJsonSafe(res);
  if (!res.ok) return { ok: false, error: toErrorMessage(body) ?? res.statusText, status: res.status, body };
  return { ok: true, data: body as T };
}

export async function apiPatch<T>(path: string, payload: unknown): Promise<ApiResult<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify(payload)
  });
  const body = await readJsonSafe(res);
  if (!res.ok) return { ok: false, error: toErrorMessage(body) ?? res.statusText, status: res.status, body };
  return { ok: true, data: body as T };
}

export async function apiPut<T>(path: string, payload: unknown): Promise<ApiResult<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify(payload)
  });
  const body = await readJsonSafe(res);
  if (!res.ok) return { ok: false, error: toErrorMessage(body) ?? res.statusText, status: res.status, body };
  return { ok: true, data: body as T };
}


function toErrorMessage(body: unknown): string | null {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (typeof body === "object") {
    const anyBody = body as Record<string, unknown>;
    const msg = anyBody.message ?? anyBody.error;
    if (typeof msg === "string") return msg;
  }
  return null;
}

export async function apiDelete<T>(path: string, payload?: unknown): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { "Accept": "application/json" };
  if (payload !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
    headers,
    body: payload !== undefined ? JSON.stringify(payload) : undefined
  });
  const body = await readJsonSafe(res);
  if (!res.ok) return { ok: false, error: toErrorMessage(body) ?? res.statusText, status: res.status, body };
  return { ok: true, data: body as T };
}
